import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {ReactComponent as FilterIcon} from 'assets/svg/filter2.svg';
import {Page} from 'components/common/Page';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Select} from 'components/ui-kit/Select';
import {ProductGroups} from 'core/constants/productGroups';
import {PRODUCT_ACTIVE_STATUS_COLORS, PRODUCT_GTIN_STATUS_COLORS} from 'core/constants/statuses';
import {listState$, showConfigBlockChanged} from 'models/registries/gtinsGrid/list';
import {editGtinApiV2Fx} from 'models/registries/gtinsGrid/upload';
import {breakpoint, colors} from 'theme';
import {UserPermission} from 'types';
import {
    ActiveStatuses,
    GtinsAccessors,
    GtinsGridFormattedRow,
    GtinsGridRawRow,
    GtinStatuses,
} from 'types/gtins';
import {useMatchMedia, usePermission} from 'utils/custom-hooks';
import {handleNil} from 'utils/handleNil';
import {
    PackageTypesOptions,
    ProductStatusesOptions,
    StatusesOptions,
} from 'utils/packageTypes-mappers';
import {mapArrayToI18nOptions, mapEnumToI18nOptions} from 'utils/select';

import {GtinsRecallForm} from '../GtinsRecallForm';
import {GtinsUploadForm} from '../GtinsUploadForm';
import {GtinsUploadList} from '../GtinsUploadList';

import {Controls} from './Controls';
import {GtinsGridList} from './List';
import {
    ControlsWrapper,
    GtinEditLink,
    HeaderControls,
    StatusWrapper,
    StatusWrapperText,
} from './styled';
import {GtinsGridTable} from './Table';

const getConfig = (t: Function) => [
    {
        id: GtinsAccessors.productGroupCode,
        header: t('pages.registries.gtins.productGroup'),
        filter: (
            <Select
                options={mapEnumToI18nOptions(ProductGroups, 'dictionaries.productGroups')}
                isClearable
                isMulti
            />
        ),
        isSortable: false,
        ignoreConfig: true,
        width: 150,
    },
    {
        id: GtinsAccessors.name,
        header: t('pages.registries.gtins.name'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
    },
    {
        id: GtinsAccessors.gtin,
        header: t('pages.registries.gtins.gtin'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
        width: 170,
    },
    {
        id: GtinsAccessors.brand,
        header: t('pages.registries.gtins.brand'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
        width: 150,
    },
    {
        id: GtinsAccessors.manufacturer,
        header: t('pages.registries.gtins.manufacturer'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
    },
    {
        id: GtinsAccessors.packageType,
        header: t('pages.registries.gtins.packageType'),
        filter: (
            <Select
                options={mapArrayToI18nOptions(
                    PackageTypesOptions,
                    'dictionaries.products.packageType',
                )}
            />
        ),
        isSortable: true,
        width: 180,
    },
    {
        id: GtinsAccessors.status,
        header: t('pages.registries.gtins.status'),
        filter: (
            <Select
                options={mapArrayToI18nOptions(
                    ProductStatusesOptions,
                    'dictionaries.productStatuses',
                )}
            />
        ),
        width: 150,
    },
    {
        id: GtinsAccessors.isActive,
        header: t('pages.registries.gtins.isActive'),
        filter: (
            <Select
                options={mapArrayToI18nOptions(
                    StatusesOptions,
                    'dictionaries.products.activeStatuses',
                )}
            />
        ),
        width: 150,
    },
];

export const GtinsGridPage = () => {
    const {t} = useTranslation();
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);

    const hasCreatePermission = usePermission([UserPermission.GTIN_CREATE]);
    const hasViewPermission = usePermission([UserPermission.GTIN_DETAIL_VIEW]);

    const {showConfigBlock} = useStore(listState$);

    const convertData = (
        data: GtinsGridRawRow[] | undefined,
        t: TFunction,
    ): GtinsGridFormattedRow[] => {
        if (!data) return [];

        const getColors = (status: GtinStatuses) => ({
            color: PRODUCT_GTIN_STATUS_COLORS[status ?? 'DEFAULT'],
        });

        const getColorsActive = (status: boolean) => ({
            color:
                PRODUCT_ACTIVE_STATUS_COLORS[
                    status === undefined
                        ? 'DEFAULT'
                        : status
                        ? ActiveStatuses.ACTIVE
                        : ActiveStatuses.INACTIVE
                ],
        });

        return data.map((elem) => ({
            [GtinsAccessors.productGroupCode]: handleNil(elem.productGroupCode, (value) => (
                <ProductGroupIcon value={value} label />
            )),
            [GtinsAccessors.name]: handleNil(elem.productName),
            [GtinsAccessors.gtin]: handleNil(elem.gtin, (value) => {
                const trimmedValue = value ? value.toString().trim() : '';
                return hasViewPermission ? (
                    <GtinEditLink onClick={() => onEdit(trimmedValue)}>{trimmedValue}</GtinEditLink>
                ) : (
                    <div>{trimmedValue}</div>
                );
            }),
            [GtinsAccessors.brand]: handleNil(elem.productTrademark),
            [GtinsAccessors.manufacturer]: handleNil(elem.manufacturerName),
            [GtinsAccessors.packageType]: handleNil(elem.packageType, (value) =>
                t('dictionaries.products.packageType.' + value),
            ),
            [GtinsAccessors.status]: handleNil(elem.status, (value) =>
                value === GtinStatuses.PUBLISHED ? (
                    <StatusWrapper {...getColors(value)}>
                        {t('dictionaries.productStatuses.PUBLISHED')}
                    </StatusWrapper>
                ) : (
                    <StatusWrapper {...getColors(value)}>
                        {t('dictionaries.productStatuses.PENDING')}
                    </StatusWrapper>
                ),
            ),
            [GtinsAccessors.isActive]: handleNil(elem.isActive, (value) =>
                value ? (
                    <StatusWrapperText {...getColorsActive(value)}>
                        {t('pages.registries.activeStatus.active')}
                    </StatusWrapperText>
                ) : (
                    <StatusWrapperText {...getColorsActive(value)}>
                        {t('pages.registries.activeStatus.inactive')}
                    </StatusWrapperText>
                ),
            ),
        }));
    };

    const onEdit = async (gtin: number) => editGtinApiV2Fx(gtin);

    // TODO wait until checks grid will be done on server
    const headerControls = hasCreatePermission ? (
        <HeaderControls>
            <ControlsWrapper>
                <Controls />
            </ControlsWrapper>
            {/*<NavigationWrapper>*/}
            {/*    <GridsNavigation />*/}
            {/*</NavigationWrapper>*/}
        </HeaderControls>
    ) : (
        undefined
    );

    const headerControlsUpd = (
        <HeaderControls>
            <ControlsWrapper>
                <Controls />
            </ControlsWrapper>
            {/*<NavigationWrapper>*/}
            {/*    <GridsNavigation />*/}
            {/*</NavigationWrapper>*/}
        </HeaderControls>
    );

    const pageHeaderControls = isMobile ? (
        <Icon
            source={FilterIcon}
            width={18}
            height={18}
            onClick={() => showConfigBlockChanged(!showConfigBlock)}
            fillColor={showConfigBlock ? colors.brandPrimary : colors.black}
        />
    ) : (
        undefined
    );

    return (
        <Page title={t('menu.Registries')} controls={pageHeaderControls}>
            {!isMobile && (
                <GtinsGridTable
                    configFunc={getConfig}
                    dataMapper={convertData}
                    headerControls={headerControlsUpd}
                    // exportConfig={exportConfig}
                />
            )}
            {isMobile && (
                <GtinsGridList
                    configFunc={getConfig}
                    dataMapper={convertData}
                    headerControls={headerControlsUpd}
                    // exportConfig={exportConfig}
                    showConfigBlock={showConfigBlock}
                />
            )}
            <GtinsUploadList />
            <GtinsUploadForm />
            <GtinsRecallForm />
        </Page>
    );
};
